import { zodResolver } from '@hookform/resolvers/zod';
import type { Scanner, Branch, Shelf, User, Tenant, DeviceModel } from '@packages/shared-types';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScannerStatusBadge } from '@/components/shared/ScannerStatusBadge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { PageHeader } from '@/components/shared/PageHeader';
import { TableCard } from '@/components/shared/TableCard';
import { TablePagination } from '@/components/shared/TablePagination';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { TableActionsMenu, type TableAction } from '@/components/shared/TableActionsMenu';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import { usePaginationFromUrl } from '@/hooks/usePaginationFromUrl';
import {
  listScannersPaginated,
  createScanner,
  deleteScanner,
  type CreateScannerPayload,
} from '@/services/scannerService';
import { listBranches } from '@/services/branchService';
import { listShelves } from '@/services/shelfService';
import { listUsers } from '@/services/userService';
import { getTenants } from '@/services/tenantService';
import { listDeviceModels } from '@/services/deviceModelService';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';
import { useAuth } from '@/hooks/useAuth';

const scannerFormSchema = z.object({
  name: z.string().min(1, 'pages.scanners.form.nameRequired'),
  deviceId: z.string().min(1, 'pages.scanners.form.deviceIdRequired'),
  type: z.enum(['handheld', 'fixed', 'standalone']),
  status: z.enum(['active', 'inactive', 'maintenance', 'charging']).optional(),
  tenantId: z.string().uuid().optional().or(z.literal('')),
  branchId: z.string().uuid().min(1, 'pages.scanners.form.branchRequired'),
  shelfId: z.string().uuid().optional().or(z.literal('')),
  assignedUserId: z.string().uuid().optional().or(z.literal('')),
  deviceModelId: z.string().optional(),
});

type ScannerFormValues = z.infer<typeof scannerFormSchema>;

const SCANNER_TYPES: Array<{ value: 'handheld' | 'fixed' | 'standalone'; labelKey: string }> = [
  { value: 'handheld', labelKey: 'pages.scanners.types.handheld' },
  { value: 'fixed', labelKey: 'pages.scanners.types.fixed' },
  { value: 'standalone', labelKey: 'pages.scanners.types.standalone' },
];

const SCANNER_STATUSES: Array<{
  value: 'active' | 'inactive' | 'maintenance' | 'charging';
  labelKey: string;
}> = [
  { value: 'active', labelKey: 'pages.scanners.statuses.active' },
  { value: 'inactive', labelKey: 'pages.scanners.statuses.inactive' },
  { value: 'maintenance', labelKey: 'pages.scanners.statuses.maintenance' },
  { value: 'charging', labelKey: 'pages.scanners.statuses.charging' },
];

export function ScannersPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeTenant } = useTenantSwitcher();
  const { user } = useAuth();
  const isRoot = user?.role === 'root';
  const title = useIndustryTranslation('pages.scanners.title');
  const description = useIndustryTranslation('pages.scanners.description');
  const createLabel = useIndustryTranslation('pages.scanners.actions.create');
  const emptyLabel = useIndustryTranslation('pages.scanners.list.empty');
  const loadingLabel = useIndustryTranslation('pages.scanners.list.loading');
  const successCreate = useIndustryTranslation('pages.scanners.success.create');
  const successDelete = useIndustryTranslation('pages.scanners.success.delete');
  const errorLoad = useIndustryTranslation('pages.scanners.error.load');
  const errorCreate = useIndustryTranslation('pages.scanners.error.create');
  const errorDelete = useIndustryTranslation('pages.scanners.error.delete');
  const branchTableLabel = useIndustryTranslation('pages.scanners.table.branch');
  const shelfTableLabel = useIndustryTranslation('pages.scanners.table.shelf');

  const [scanners, setScanners] = useState<Scanner[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [deviceModels, setDeviceModels] = useState<DeviceModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isLoadingShelves, setIsLoadingShelves] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [isLoadingDeviceModels, setIsLoadingDeviceModels] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedScanner, setSelectedScanner] = useState<Scanner | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const pagination = usePaginationFromUrl();

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    control: controlCreate,
    watch: watchCreate,
    setValue: setValueCreate,
    formState: { errors: createErrors, isSubmitting: isCreating },
    reset: resetCreate,
  } = useForm<ScannerFormValues>({
    resolver: zodResolver(scannerFormSchema),
    defaultValues: {
      name: '',
      deviceId: '',
      type: 'handheld',
      status: 'inactive',
      tenantId: '',
      branchId: '',
      shelfId: '',
      assignedUserId: '',
      deviceModelId: '__none__',
    },
  });

  const selectedBranchId = watchCreate('branchId');
  const selectedTenantId = watchCreate('tenantId');

  // Filter shelves by selected branch
  const availableShelves = useMemo(() => {
    if (!selectedBranchId) return [];
    return shelves.filter((shelf) => shelf.branchId === selectedBranchId);
  }, [shelves, selectedBranchId]);

  const loadScanners = useCallback(async (): Promise<void> => {
    // For non-root users, tenant should always exist due to TenantRequiredRoute
    // But we check anyway for safety
    if (!activeTenant && !isRoot) {
      setIsLoading(false);
      setScanners([]);
      return;
    }

    // Root users can load scanners even without active tenant (support mode - shows all scanners)
    setIsLoading(true);
    setError(null);
    try {
      const result = await listScannersPaginated({
        page: pagination.page,
        limit: pagination.limit,
      });
      setScanners(result.data);
      pagination.setTotal(result.total);
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : errorLoad;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [errorLoad, isRoot, activeTenant?.id, pagination.page, pagination.limit]);

  useEffect(() => {
    void loadScanners();
  }, [loadScanners]);

  // Fetch tenants (only for root users)
  useEffect(() => {
    if (!isRoot) return;
    const loadTenants = async (): Promise<void> => {
      setIsLoadingTenants(true);
      try {
        const data = await getTenants();
        setTenants(data);
      } catch (err) {
        console.error('Failed to load tenants', err);
      } finally {
        setIsLoadingTenants(false);
      }
    };
    void loadTenants();
  }, [isRoot]);

  // Fetch branches - filter by selected tenant or current tenant
  useEffect(() => {
    // Only load branches if we have a tenant context
    const effectiveTenantId = isRoot && selectedTenantId ? selectedTenantId : activeTenant?.id;
    if (!effectiveTenantId) {
      setBranches([]);
      return;
    }

    const loadBranches = async (): Promise<void> => {
      setIsLoadingBranches(true);
      try {
        const data = await listBranches(effectiveTenantId);
        // Filter branches by tenant (API might return all branches for root users)
        const filtered = data.filter((branch) => branch.tenantId === effectiveTenantId);
        setBranches(filtered);
      } catch (err) {
        console.error('Failed to load branches', err);
        setBranches([]);
      } finally {
        setIsLoadingBranches(false);
      }
    };
    void loadBranches();
  }, [isRoot, selectedTenantId, activeTenant?.id]);

  // When root/support with no tenant selected, load branches for every tenant that has scanners so the table can show branch names
  useEffect(() => {
    const supportModeNoTenant =
      isRoot && !activeTenant && !selectedTenantId && scanners.length > 0;
    if (!supportModeNoTenant) return;

    const tenantIds = [...new Set(scanners.map((s) => s.tenantId).filter(Boolean))] as string[];
    if (tenantIds.length === 0) return;

    const loadBranchesForScannerTenants = async (): Promise<void> => {
      setIsLoadingBranches(true);
      try {
        const results = await Promise.all(tenantIds.map((tenantId) => listBranches(tenantId)));
        const merged = results.flat();
        setBranches(merged);
      } catch (err) {
        console.error('Failed to load branches for scanner tenants', err);
        setBranches([]);
      } finally {
        setIsLoadingBranches(false);
      }
    };
    void loadBranchesForScannerTenants();
  }, [isRoot, activeTenant?.id, selectedTenantId, scanners]);

  // Fetch shelves
  useEffect(() => {
    const loadShelves = async (): Promise<void> => {
      setIsLoadingShelves(true);
      try {
        const data = await listShelves();
        setShelves(data);
      } catch (err) {
        console.error('Failed to load shelves', err);
      } finally {
        setIsLoadingShelves(false);
      }
    };
    void loadShelves();
  }, [activeTenant?.id]);

  // Fetch device models
  useEffect(() => {
    const loadDeviceModels = async (): Promise<void> => {
      setIsLoadingDeviceModels(true);
      try {
        const data = await listDeviceModels();
        setDeviceModels(data);
      } catch (err) {
        console.error('Failed to load device models', err);
      } finally {
        setIsLoadingDeviceModels(false);
      }
    };
    void loadDeviceModels();
  }, []);

  // Fetch users - filter by selected tenant or current tenant
  useEffect(() => {
    const loadUsers = async (): Promise<void> => {
      setIsLoadingUsers(true);
      try {
        // Use selected tenant from form if root user, otherwise use activeTenant
        const tenantIdForUsers = isRoot && selectedTenantId ? selectedTenantId : activeTenant?.id;
        const data = await listUsers(tenantIdForUsers);
        setUsers(data);
      } catch (err) {
        console.error('Failed to load users', err);
      } finally {
        setIsLoadingUsers(false);
      }
    };
    void loadUsers();
  }, [isRoot, selectedTenantId, activeTenant?.id]);

  const handleCreate = async (values: ScannerFormValues): Promise<void> => {
    try {
      const payload: CreateScannerPayload = {
        name: values.name,
        deviceId: values.deviceId,
        type: values.type,
        status: values.status || 'active',
        deviceModelId:
          values.deviceModelId && values.deviceModelId !== '__none__'
            ? values.deviceModelId
            : undefined,
        branchId: values.branchId,
        shelfId: values.shelfId || undefined,
        assignedUserId: values.assignedUserId || undefined,
        // Include tenantId if root user selected it in form, otherwise backend uses user.tenantId
        tenantId: isRoot && values.tenantId ? values.tenantId : undefined,
      };
      const newScanner = await createScanner(payload);
      setIsCreateDialogOpen(false);
      resetCreate();
      toast.success(successCreate);
      // Reload scanners to show the newly created one
      await loadScanners();
      // Navigate to scanner edit page
      navigate(`/scanners/${newScanner.id}/edit`);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : errorCreate;
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!selectedScanner) return;
    setIsDeleting(true);
    try {
      await deleteScanner(selectedScanner.id);
      toast.success(successDelete);
      setIsDeleteDialogOpen(false);
      setSelectedScanner(null);
      await loadScanners();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : errorDelete,
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = useCallback((scanner: Scanner) => {
    setSelectedScanner(scanner);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleRowClick = useCallback(
    (scannerId: string) => {
      return () => navigate(`/scanners/${scannerId}`);
    },
    [navigate],
  );

  const handleCreateButtonClick = useCallback(() => {
    setIsCreateDialogOpen(true);
  }, []);

  const handleEditClick = useCallback(
    (scannerId: string) => {
      return () => navigate(`/scanners/${scannerId}/edit`);
    },
    [navigate],
  );

  const handleDeleteClick = useCallback(
    (scanner: Scanner) => {
      return () => openDeleteDialog(scanner);
    },
    [openDeleteDialog],
  );

  const handleDetailsClick = useCallback(
    (scannerId: string) => {
      return () => navigate(`/scanners/${scannerId}`);
    },
    [navigate],
  );

  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString();
  };

  const formatBatteryLevel = (level: number | null | undefined): string => {
    if (level === null || level === undefined) return '—';
    return `${level}%`;
  };

  const createDialogTitle = useIndustryTranslation(
    'pages.scanners.dialogs.create.title',
  );
  const createDialogDescription = useIndustryTranslation(
    'pages.scanners.dialogs.create.description',
  );
  const deleteDialogTitle = useIndustryTranslation(
    'pages.scanners.dialogs.delete.title',
  );
  const branchLabel = useIndustryTranslation('pages.scanners.form.branch');
  const selectBranchLabel = t('pages.scanners.form.selectBranch');

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={title}
        description={description}
        actions={
          isRoot ? (
            <Button onClick={handleCreateButtonClick}>
              <Plus className="h-4 w-4" />
              {createLabel}
            </Button>
          ) : null
        }
      />

      {error && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="p-4">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <TableCard>
        {isLoading ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <LoadingLogo size="sm" />
            <span className="text-sm text-muted-foreground">{loadingLabel}</span>
          </div>
        ) : scanners.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {emptyLabel}
          </div>
        ) : (
          <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('pages.scanners.table.name')}</TableHead>
                <TableHead>{t('pages.scanners.table.deviceId')}</TableHead>
                <TableHead>{t('pages.scanners.table.type')}</TableHead>
                <TableHead>{t('pages.scanners.table.status')}</TableHead>
                {isRoot && !activeTenant && <TableHead>{t('pages.scanners.table.tenant')}</TableHead>}
                <TableHead>{branchTableLabel}</TableHead>
                <TableHead>{shelfTableLabel}</TableHead>
                <TableHead>{t('pages.scanners.table.batteryLevel')}</TableHead>
                <TableHead>{t('pages.scanners.table.lastCheckin')}</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scanners.map((scanner) => {
                const branch = branches.find((b) => b.id === scanner.branchId);
                const shelf = shelves.find((s) => s.id === scanner.shelfId);
                const actions: TableAction[] = isRoot && !activeTenant
                  ? [
                      {
                        label: t('pages.scanners.actions.edit'),
                        icon: Pencil,
                        onClick: handleEditClick(scanner.id),
                      },
                      {
                        label: t('pages.scanners.actions.delete'),
                        icon: Trash2,
                        onClick: handleDeleteClick(scanner),
                        variant: 'destructive',
                      },
                    ]
                  : [
                      {
                        label: t('pages.scanners.actions.details'),
                        icon: Pencil,
                        onClick: handleDetailsClick(scanner.id),
                      },
                    ];
                return (
                  <TableRow
                    key={scanner.id}
                    onRowClick={handleRowClick(scanner.id)}
                  >
                    <TableCell className="font-medium">{scanner.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {scanner.deviceId}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {t(`pages.scanners.types.${scanner.type}`)}
                    </TableCell>
                    <TableCell>
                      <ScannerStatusBadge status={scanner.status} />
                    </TableCell>
                    {isRoot && !activeTenant && (
                      <TableCell className="text-muted-foreground">
                        {tenants.find((t) => t.id === scanner.tenantId)?.name || '—'}
                      </TableCell>
                    )}
                    <TableCell className="text-muted-foreground">
                      {branch?.name || '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {shelf?.name || (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatBatteryLevel(scanner.batteryLevel)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(scanner.lastCheckinAt)}
                    </TableCell>
                    <TableCell>
                      <TableActionsMenu actions={actions} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <TablePagination
            pagination={pagination}
            rowsPerPageLabel={t('pages.common.pagination.rowsPerPage')}
            pageOfLabel={t('pages.common.pagination.pageOf', {
              current: pagination.page,
              total: pagination.totalPages,
            })}
            onRefresh={() => void loadScanners()}
            isRefreshing={isLoading}
          />
          </>
        )}
      </TableCard>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createDialogTitle}</DialogTitle>
            <DialogDescription>{createDialogDescription}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitCreate(handleCreate)}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">
                  {t('pages.scanners.form.name')} *
                </Label>
                <Input
                  id="create-name"
                  {...registerCreate('name')}
                  placeholder={t('pages.scanners.form.name')}
                />
                {createErrors.name && (
                  <p className="text-sm text-destructive">
                    {t(createErrors.name.message || 'pages.scanners.form.nameRequired')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-deviceId">
                  {t('pages.scanners.form.machineId')} *
                </Label>
                <Input
                  id="create-deviceId"
                  {...registerCreate('deviceId')}
                  placeholder={t('pages.scanners.form.machineId')}
                />
                <p className="text-sm text-muted-foreground">
                  {t('pages.scanners.form.machineIdHelper')}
                </p>
                {createErrors.deviceId && (
                  <p className="text-sm text-destructive">
                    {t(createErrors.deviceId.message || 'pages.scanners.form.deviceIdRequired')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-type">
                  {t('pages.scanners.form.type')} *
                </Label>
                <Controller
                  name="type"
                  control={controlCreate}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger id="create-type">
                        <SelectValue placeholder={t('pages.scanners.form.type')} />
                      </SelectTrigger>
                      <SelectContent>
                        {SCANNER_TYPES.map((scannerType) => (
                          <SelectItem
                            key={scannerType.value}
                            value={scannerType.value}
                          >
                            {t(scannerType.labelKey)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {createErrors.type && (
                  <p className="text-sm text-destructive">
                    {t(createErrors.type.message || 'pages.scanners.form.typeRequired')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-status">
                  {t('pages.scanners.form.status')}
                </Label>
                <Controller
                  name="status"
                  control={controlCreate}
                  render={({ field }) => (
                    <Select
                      value={field.value || 'active'}
                      onValueChange={(value) => field.onChange(value as typeof field.value)}
                    >
                      <SelectTrigger id="create-status">
                        <SelectValue placeholder={t('pages.scanners.form.status')} />
                      </SelectTrigger>
                      <SelectContent>
                        {SCANNER_STATUSES.map((status) => (
                          <SelectItem
                            key={status.value}
                            value={status.value}
                          >
                            {t(status.labelKey)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-deviceModel">
                  {t('pages.scanners.form.deviceModel')}
                </Label>
                <Controller
                  name="deviceModelId"
                  control={controlCreate}
                  render={({ field }) => (
                    <Select
                      value={field.value || '__none__'}
                      onValueChange={(value) => field.onChange(value || '__none__')}
                      disabled={isLoadingDeviceModels}
                    >
                      <SelectTrigger id="create-deviceModel">
                        <SelectValue placeholder={t('pages.scanners.form.selectDeviceModel')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {deviceModels.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            {model.name} ({model.slug})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              {isRoot && !activeTenant && (
                <div className="space-y-2">
                  <Label htmlFor="create-tenant">
                    {t('pages.scanners.form.tenant')} *
                  </Label>
                  <Controller
                    name="tenantId"
                    control={controlCreate}
                    render={({ field }) => (
                      <Select
                        value={field.value || undefined}
                        onValueChange={(value) => {
                          field.onChange(value || '');
                          // Reset branch and shelf when tenant changes
                          setValueCreate('branchId', '');
                          setValueCreate('shelfId', '');
                        }}
                        disabled={isLoadingTenants}
                      >
                        <SelectTrigger id="create-tenant">
                          <SelectValue placeholder={t('pages.scanners.form.selectTenant')} />
                        </SelectTrigger>
                        <SelectContent>
                          {tenants.length === 0 ? (
                            <SelectItem value="none" disabled>
                              {t('pages.scanners.form.selectTenant')}
                            </SelectItem>
                          ) : (
                            tenants.map((tenant) => (
                              <SelectItem key={tenant.id} value={tenant.id}>
                                {tenant.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {createErrors.tenantId && (
                    <p className="text-sm text-destructive">
                      {t(createErrors.tenantId.message || 'pages.scanners.form.tenantRequired')}
                    </p>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="create-branch">
                  {branchLabel} *
                </Label>
                <Controller
                  name="branchId"
                  control={controlCreate}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        // Reset shelf when branch changes
                        setValueCreate('shelfId', '');
                      }}
                      disabled={isLoadingBranches || (isRoot && !activeTenant && !selectedTenantId)}
                    >
                      <SelectTrigger id="create-branch">
                        <SelectValue placeholder={selectBranchLabel} />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map((branch) => (
                          <SelectItem key={branch.id} value={branch.id}>
                            {branch.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {createErrors.branchId && (
                  <p className="text-sm text-destructive">
                    {t(createErrors.branchId.message || 'pages.scanners.form.branchRequired')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-shelf">
                  {t('pages.scanners.form.shelf')}
                </Label>
                <Controller
                  name="shelfId"
                  control={controlCreate}
                  render={({ field }) => (
                    <Select
                      value={field.value || undefined}
                      onValueChange={(value) => field.onChange(value || '')}
                      disabled={isLoadingShelves || !selectedBranchId}
                    >
                      <SelectTrigger id="create-shelf">
                        <SelectValue placeholder={t('pages.scanners.form.selectShelf')} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableShelves.length === 0 ? (
                          <SelectItem value="none" disabled>
                            {selectedBranchId
                              ? t('pages.scanners.form.selectShelf')
                              : t('pages.scanners.form.selectBranchFirst')}
                          </SelectItem>
                        ) : (
                          availableShelves.map((shelf) => (
                            <SelectItem key={shelf.id} value={shelf.id}>
                              {shelf.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-assignedUser">
                  {t('pages.scanners.form.assignedUser')}
                </Label>
                <Controller
                  name="assignedUserId"
                  control={controlCreate}
                  render={({ field }) => (
                    <Select
                      value={field.value || undefined}
                      onValueChange={(value) => field.onChange(value || '')}
                      disabled={isLoadingUsers}
                    >
                      <SelectTrigger id="create-assignedUser">
                        <SelectValue placeholder={t('pages.scanners.form.selectUser')} />
                      </SelectTrigger>
                      <SelectContent>
                        {users.length === 0 ? (
                          <SelectItem value="none" disabled>
                            {t('pages.scanners.form.selectUser')}
                          </SelectItem>
                        ) : (
                          users.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.firstName} {user.lastName} ({user.email})
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetCreate();
                }}
              >
                {t('pages.scanners.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating
                  ? t('pages.scanners.actions.saving')
                  : createLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title={deleteDialogTitle}
        description={t('pages.scanners.dialogs.delete.description', {
          name: selectedScanner?.name ?? '',
        })}
        confirmText={t('pages.scanners.actions.delete')}
        cancelText={t('pages.scanners.actions.cancel')}
        variant="destructive"
        isLoading={isDeleting}
        onConfirm={handleDelete}
      />
    </div>
  );
}
